import { useTranslation } from 'react-i18next'
import {
  conversionFactors,
  formatUnit,
  unitLabel,
  type EnvelopePoint,
  type FuelQuantity,
  type LoadingStation,
  type UnitSystem,
  type WorkedState,
  type WorkedWeightBalance,
} from '@jupi/shared'
import { formatDate } from '@jupi/ui'
import { Envelope } from './PilotLogSheet'
import { WORKED_SHEET_CSS } from './workedSheetCss'

// The fully worked weight & balance sheet (#199): every step of the sum
// written out so a pilot can check the figures on the plan from first
// principles. Plain elements and one scoped stylesheet, like the pilot log.

const STATION_LABEL: Record<LoadingStation, string> = {
  empty: 'performance.emptyWeight',
  front: 'performance.armFrontPax',
  rear: 'performance.armRearPax',
  cargo: 'performance.armCargo',
  fuelMain: 'performance.armFuelMain',
  fuelRes: 'performance.armFuelRes',
}

export interface WorkedWeightSheetProps {
  worked: WorkedWeightBalance
  envelope: EnvelopePoint[]
  mtowKg: number | null
  registration: string
  aircraftType: string | null
  pilotName: string
  /** Today, YYYY-MM-DD in the club's time zone. */
  today: string
  system: UnitSystem
}

const f = (n: number) => n.toFixed(1)

export function WorkedWeightSheet({ worked, envelope, mtowKg, registration, aircraftType, pilotName, today, system }: WorkedWeightSheetProps) {
  const { t } = useTranslation()
  const m = (kg: number) => formatUnit('mass', kg, system, 1)
  const a = (arm: number) => formatUnit('arm', arm, system, 3)
  const mo = (moment: number) => formatUnit('moment', moment, system, 1)
  const mu = unitLabel('mass', system)
  const au = unitLabel('arm', system)
  const mou = unitLabel('moment', system)
  const { ramp, takeoff, landing, ladder: l, assumptions: as } = worked
  const sumRows = worked.rows.filter((row) => row.station === 'empty' || row.kg > 0)
  const factors = conversionFactors(as.density)
  const armMain = worked.rows.find((row) => row.station === 'fuelMain')!.arm
  const flow = {
    usg: as.flowUsgPerHour,
    litres: as.flowUsgPerHour * as.litresPerUsg,
    kgPerHour: as.flowUsgPerHour * as.litresPerUsg * as.density,
  }
  const fuelRow = (label: string, q: FuelQuantity, sign = '') => (
    <tr>
      <td>{label}</td>
      <td className="num">{sign}{f(q.litres)} l</td>
      <td className="num">{sign}{f(q.usg)} USG</td>
      <td className="num">{sign}{m(q.kg)} {mu}</td>
    </tr>
  )
  const massRow = (label: string, kg: number, how: string, key = false) => (
    <tr className={key ? 'total' : undefined}>
      <td>{label}</td>
      <td className="num">{how}</td>
      <td className="num">{m(kg)} {mu}</td>
    </tr>
  )

  return (
    <article className="wb-sheet" aria-label={t('plan.worked.title')}>
      <style>{WORKED_SHEET_CSS}</style>
      <header className="hd">
        <h1>{t('plan.worked.title')}</h1>
        <span className="mono">
          <b>{registration}</b>
        </span>
      </header>
      <div className="meta">
        {aircraftType ? <span>{aircraftType}</span> : null}
        <span>
          {t('plan.sheet.date')} <b>{formatDate(today)}</b>
        </span>
        <span>
          {t('plan.sheet.pic')} <b>{pilotName}</b>
        </span>
        {mtowKg ? (
          <span>
            {t('plan.weight.mtow')} <b>{m(mtowKg)} {mu}</b>
          </span>
        ) : null}
      </div>

      <h2>
        <span className="step">1</span>
        {t('plan.worked.assumptions')}
      </h2>
      <table>
        <tbody>
          <tr>
            <td>{t('plan.worked.fuelFlow')}</td>
            <td className="num">
              {f(flow.usg)} USG/h × {as.litresPerUsg.toFixed(4)} = {f(flow.litres)} l/h × {as.density.toFixed(2)} = {f(flow.kgPerHour)} kg/h
            </td>
          </tr>
          <tr>
            <td>{t('plan.worked.flightTime')}</td>
            <td className="num">{as.flightMinutes} min</td>
          </tr>
          <tr>
            <td>{t('plan.worked.taxiTime')}</td>
            <td className="num">{as.taxiMinutes} min</td>
          </tr>
          <tr>
            <td>{t('plan.worked.taxiFuelRate')}</td>
            <td className="num">{f(as.taxiLitresPerTenMin)} l / 10 min</td>
          </tr>
        </tbody>
      </table>

      <h2>
        <span className="step">2</span>
        {t('plan.worked.factors')}
      </h2>
      <table className="factors">
        <tbody>
          <tr>
            <td>{t('plan.worked.factorMass')}</td>
            <td className="num">1 kg = {factors.lbPerKg.toFixed(4)} lb</td>
            <td className="num">1 lb = {factors.kgPerLb.toFixed(6)} kg</td>
          </tr>
          <tr>
            <td>{t('plan.worked.factorVolume')}</td>
            <td className="num">1 USG = {factors.litresPerUsg.toFixed(4)} l</td>
            <td className="num">1 l = {factors.usgPerLitre.toFixed(4)} USG</td>
          </tr>
          <tr>
            <td>{t('plan.worked.factorLength')}</td>
            <td className="num">1 m = {factors.inchesPerMetre.toFixed(4)} in</td>
            <td className="num">1 in = {factors.metresPerInch.toFixed(4)} m</td>
          </tr>
          <tr>
            <td>{t('plan.worked.factorMoment')}</td>
            <td className="num" colSpan={2}>
              1 kg·m = {factors.lbInPerKgm.toFixed(2)} lb·in
            </td>
          </tr>
          <tr>
            <td>{t('plan.worked.factorDensity')}</td>
            <td className="num">{factors.densityKgPerLitre.toFixed(2)} kg/l</td>
            <td className="num">
              {factors.densityKgPerUsg.toFixed(3)} kg/USG = {factors.densityLbPerUsg.toFixed(2)} lb/USG
            </td>
          </tr>
        </tbody>
      </table>

      <h2>
        <span className="step">3</span>
        {t('plan.worked.fuelToMass')}
      </h2>
      <p className="how">{t('plan.worked.fuelToMassHow')}</p>
      <table>
        <thead>
          <tr>
            <th>{t('plan.worked.tank')}</th>
            <th className="num">{t('plan.worked.loaded')}</th>
            <th className="num">{t('plan.worked.litres')}</th>
            <th className="num">{t('plan.worked.density')}</th>
            <th className="num">{t('units.mass', { unit: mu })}</th>
          </tr>
        </thead>
        <tbody>
          {worked.fuel
            .filter((tank) => tank.amount > 0)
            .map((tank) => (
              <tr key={tank.station}>
                <td>{t(STATION_LABEL[tank.station])}</td>
                <td className="num">
                  {tank.amount.toFixed(1)} {tank.unit === 'usg' ? 'USG' : 'l'}
                </td>
                <td className="num">
                  {tank.unit === 'usg' ? `${tank.amount.toFixed(1)} × ${as.litresPerUsg.toFixed(4)} = ` : ''}
                  {tank.litres.toFixed(1)} l
                </td>
                <td className="num">× {tank.density.toFixed(2)} kg/l</td>
                <td className="num">{m(tank.kg)}</td>
              </tr>
            ))}
          {worked.fuel.every((tank) => tank.amount <= 0) ? (
            <tr>
              <td colSpan={5}>{t('plan.worked.noFuel')}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <h2>
        <span className="step">4</span>
        {t('plan.worked.rampLoading')}
      </h2>
      <p className="how">{t('plan.worked.momentHow')}</p>
      <table>
        <thead>
          <tr>
            <th>{t('plan.weight.station')}</th>
            <th className="num">{t('units.mass', { unit: mu })}</th>
            <th className="num">{t('units.arm', { unit: au })}</th>
            <th className="num">{t('units.moment', { unit: mou })}</th>
          </tr>
        </thead>
        <tbody>
          {sumRows.map((row) => (
            <tr key={row.station}>
              <td>{t(STATION_LABEL[row.station])}</td>
              <td className="num">{m(row.kg)}</td>
              <td className="num">{a(row.arm)}</td>
              <td className="num">
                {m(row.kg)} × {a(row.arm)} = {mo(row.moment)}
              </td>
            </tr>
          ))}
          <tr className="total">
            <td>{t('plan.worked.sum')}</td>
            <td className="num">{m(ramp.kg)}</td>
            <td className="num">{ramp.cgArm !== null ? a(ramp.cgArm) : '–'}</td>
            <td className="num">{mo(ramp.moment)}</td>
          </tr>
        </tbody>
      </table>
      <CgLine label={t('plan.worked.cgRamp')} state={ramp} m={m} a={a} mo={mo} mu={mu} au={au} mou={mou} />

      <h2>
        <span className="step">5</span>
        {t('plan.worked.weights')}
      </h2>
      <p className="how">{t('plan.worked.weightsHow')}</p>
      <table>
        <tbody>
          {massRow(t('plan.worked.basicEmpty'), l.basicEmptyKg, '')}
          {massRow(t('plan.worked.payload'), l.payloadKg, `${m(worked.rows[1]!.kg)} + ${m(worked.rows[2]!.kg)} + ${m(worked.rows[3]!.kg)}`)}
          {massRow(t('plan.worked.zeroFuel'), l.zeroFuelKg, `${m(l.basicEmptyKg)} + ${m(l.payloadKg)}`, true)}
          {massRow(t('plan.worked.fuelLoaded'), l.fuelLoaded.kg, `${f(l.fuelLoaded.litres)} l = ${f(l.fuelLoaded.usg)} USG`)}
          {massRow(t('plan.worked.ramp'), l.rampKg, `${m(l.zeroFuelKg)} + ${m(l.fuelLoaded.kg)}`, true)}
          {massRow(
            t('plan.worked.taxiFuel', { minutes: l.taxi.minutes }),
            l.taxi.kg,
            `${f(as.taxiLitresPerTenMin)} l × ${l.taxi.minutes}/10 = ${f(l.taxi.litres)} l = ${f(l.taxi.usg)} USG`,
          )}
          {massRow(t('plan.worked.takeoffWeight'), l.takeoffKg, `${m(l.rampKg)} − ${m(l.taxi.kg)}`, true)}
          {massRow(
            t('plan.worked.tripFuelMin', { minutes: l.trip.minutes }),
            l.trip.kg,
            `${as.flowUsgPerHour.toFixed(1)} × ${l.trip.minutes}/60 = ${f(l.trip.usg)} USG = ${f(l.trip.litres)} l`,
          )}
          {massRow(t('plan.worked.landingWeight'), l.landingKg, `${m(l.takeoffKg)} − ${m(l.trip.kg)}`, true)}
        </tbody>
      </table>

      <h2>
        <span className="step">6</span>
        {t('plan.worked.fuelBurn')}
      </h2>
      <table>
        <thead>
          <tr>
            <th />
            <th className="num">l</th>
            <th className="num">USG</th>
            <th className="num">{mu}</th>
          </tr>
        </thead>
        <tbody>
          {fuelRow(t('plan.worked.fuelLoaded'), l.fuelLoaded)}
          {fuelRow(t('plan.worked.taxiFuel', { minutes: l.taxi.minutes }), l.taxi, '− ')}
          {fuelRow(t('plan.worked.tripFuelMin', { minutes: l.trip.minutes }), l.trip, '− ')}
          {fuelRow(t('plan.worked.landingFuel'), l.landingFuel)}
        </tbody>
      </table>

      <h2>
        <span className="step">7</span>
        {t('plan.worked.cgShift')}
      </h2>
      <p className="how">{t('plan.worked.burnHow')}</p>
      <table>
        <tbody>
          <tr>
            <td>{t('plan.worked.momentOutTaxi')}</td>
            <td className="num">
              {m(l.taxi.kg)} × {a(armMain)} = {mo(l.taxi.moment)} {mou}
            </td>
          </tr>
          <tr>
            <td>{t('plan.worked.momentTakeoff')}</td>
            <td className="num">
              {mo(ramp.moment)} − {mo(l.taxi.moment)} = {mo(takeoff.moment)} {mou}
            </td>
          </tr>
          <tr>
            <td>{t('plan.worked.momentOutTrip')}</td>
            <td className="num">
              {m(l.trip.kg)} × {a(armMain)} = {mo(l.trip.moment)} {mou}
            </td>
          </tr>
          <tr>
            <td>{t('plan.worked.momentLanding')}</td>
            <td className="num">
              {mo(takeoff.moment)} − {mo(l.trip.moment)} = {mo(landing.moment)} {mou}
            </td>
          </tr>
        </tbody>
      </table>
      <CgLine label={t('plan.worked.cgTakeoff')} state={takeoff} m={m} a={a} mo={mo} mu={mu} au={au} mou={mou} />
      <CgLine label={t('plan.worked.cgLanding')} state={landing} m={m} a={a} mo={mo} mu={mu} au={au} mou={mou} />

      <h2>
        <span className="step">8</span>
        {t('plan.worked.limits')}
      </h2>
      <p className="how">{t('plan.worked.limitsHow')}</p>
      <table>
        <thead>
          <tr>
            <th />
            <th className="num">{t('units.mass', { unit: mu })}</th>
            <th className="num">{t('plan.worked.mtowMargin')}</th>
            <th className="num">{t('plan.worked.forwardLimit', { unit: au })}</th>
            <th className="num">{t('plan.weight.cg')} ({au})</th>
            <th className="num">{t('plan.worked.aftLimit', { unit: au })}</th>
            <th>{t('plan.worked.result')}</th>
          </tr>
        </thead>
        <tbody>
          <LimitRow label={t('plan.weight.takeoff')} state={takeoff} m={m} a={a} />
          <LimitRow label={t('plan.weight.landing')} state={landing} m={m} a={a} />
        </tbody>
      </table>
      <div className={`verdict${worked.ok ? '' : ' bad'}`}>{worked.ok ? t('plan.editor.wbOk') : t('plan.editor.wbNotOk')}</div>

      <h2>
        <span className="step">9</span>
        {t('performance.envelope')}
      </h2>
      <div className="chartbox">
        <Envelope
          bothUnits
          envelope={envelope}
          takeoff={takeoff.cgArm !== null ? { arm: takeoff.cgArm, kg: takeoff.kg } : null}
          landing={landing.cgArm !== null ? { arm: landing.cgArm, kg: landing.kg } : null}
          labels={{ takeoff: t('plan.weight.takeoff'), landing: t('plan.weight.landing'), mass: t('units.mass', { unit: 'kg' }) }}
        />
      </div>

      <div className="sign">
        <div>{t('plan.worked.checkedBy')}</div>
        <div>{t('plan.worked.signature')}</div>
      </div>
      <p className="note">{t('plan.worked.rounding')}</p>
    </article>
  )
}

interface Fmt {
  m: (n: number) => string
  a: (n: number) => string
  mo: (n: number) => string
  mu: string
  au: string
  mou: string
}

function CgLine({ label, state, m, a, mo, mu, au, mou }: Fmt & { label: string; state: WorkedState }) {
  return (
    <p className="formula mono">
      {label}: {mo(state.moment)} {mou} ÷ {m(state.kg)} {mu} = <b>{state.cgArm !== null ? a(state.cgArm) : '–'} {au}</b>
    </p>
  )
}

function LimitRow({ label, state, m, a }: { label: string; state: WorkedState; m: (n: number) => string; a: (n: number) => string }) {
  const { t } = useTranslation()
  const ok = state.withinEnvelope && state.withinMtow
  return (
    <tr>
      <td>{label}</td>
      <td className="num">{m(state.kg)}</td>
      <td className={`num ${state.withinMtow ? '' : 'bad'}`}>{state.mtowMarginKg !== null ? m(state.mtowMarginKg) : '–'}</td>
      <td className="num">{state.limits ? a(state.limits.forward) : '–'}</td>
      <td className="num">
        <b>{state.cgArm !== null ? a(state.cgArm) : '–'}</b>
      </td>
      <td className="num">{state.limits ? a(state.limits.aft) : '–'}</td>
      <td className={ok ? 'ok' : 'bad'}>{ok ? t('plan.worked.within') : !state.withinMtow ? t('plan.weight.overMtow') : t('plan.weight.outsideEnvelope')}</td>
    </tr>
  )
}
